// Department role model (hospital / NABH). The backend sends `Capabilities.Role` at login:
// { RoleModelEnabled, RoleKind, CanAudit, CanManageTemplates, CanManageUsers, CanViewAllReports, IsDepartmentUser,
//   ReadOnly, DepartmentIds, OwnedDepartmentIds, AuditDepartmentIds, TemplateIds }.
// Only companies that opted in get RoleModelEnabled = true. For every other company (or an older backend that sends
// no Role) these helpers answer "allowed" so the existing screens behave exactly as before - the server stays the
// authority and refuses with 403 ROLE_NOT_ALLOWED.
import { SHOW_REPORT_APPROVAL_FLOW } from './constant/common'

const parseBool = (value, fallback = false) => {
    if (value === true || value === false) return value
    if (value == null) return fallback
    const text = String(value).trim().toLowerCase()
    if (text === 'true' || text === '1') return true
    if (text === 'false' || text === '0') return false
    return fallback
}

export const ROLE_KIND = {
    Admin: 'Admin',
    Auditor: 'Auditor',
    DepartmentUser: 'DepartmentUser',
    Viewer: 'Viewer',
    TemplateManager: 'TemplateManager',
    Legacy: 'Legacy',
}

export const ROLE_KIND_LABELS = {
    Admin: 'Admin',
    Auditor: 'Auditor',
    DepartmentUser: 'Department user',
    Viewer: 'Viewer',
    TemplateManager: 'Template manager',
}

export const ROLE_REFUSAL_CODE = 'ROLE_NOT_ALLOWED'

/** The Role block when the company uses the department role model, else null. */
export const getRoleModel = user => {
    const role = user?.Capabilities?.Role
    if (!role || !parseBool(role.RoleModelEnabled, false)) return null
    return role
}

export const isRoleModelOn = user => !!getRoleModel(user)

export const getRoleKind = user => getRoleModel(user)?.RoleKind || null

const roleAllows = (user, key) => {
    const role = getRoleModel(user)
    if (!role) return true
    return parseBool(role[key], false)
}

/** May start audits / tasks / schedules (role model only restricts; otherwise true). */
export const canStartAuditsByRole = user => roleAllows(user, 'CanAudit')

/** Template builder, clone, import, add to library. */
export const canManageTemplatesByRole = user =>
    roleAllows(user, 'CanManageTemplates')

/** Users, invites, department / template assignments. */
export const canManageUsersByRole = user => roleAllows(user, 'CanManageUsers')

/** Add / change / remove departments (customers): Admins only on the role model (always true for other companies). */
export const canManageDepartmentsByRole = user => {
    const role = getRoleModel(user)
    return !role || role.RoleKind === ROLE_KIND.Admin
}

/** Viewer, or a department user who audits nothing: everything read-only. */
export const isReadOnlyRole = user => {
    const role = getRoleModel(user)
    return !!role && parseBool(role.ReadOnly, false)
}

export const isDepartmentUserRole = user => {
    const role = getRoleModel(user)
    return !!role && parseBool(role.IsDepartmentUser, false)
}

/** Same rule as the backend's AuditController.CanApprove (company admin approval, all report lists). */
export const isApprovalAdmin = user =>
    !!user &&
    (user.IsSuperAdmin === true ||
        user.IsAdmin === true ||
        Number(user.RoleId ?? user.RoleID) === 3)

/**
 * The report-approval screens (list, detail, workflow config) were behind a global flag. Companies on the role model
 * use in-app approvals (quality head as approver), so they get them regardless of the flag.
 */
export const isReportApprovalFlowEnabled = user =>
    SHOW_REPORT_APPROVAL_FLOW || isRoleModelOn(user)

/** A 403 refusal from the role model (body { ErrorCode: 'ROLE_NOT_ALLOWED', Message }). */
export const isRoleRefusal = (status, data) =>
    status === 403 &&
    !!data &&
    typeof data === 'object' &&
    (data.ErrorCode || data.errorCode || data.code) === ROLE_REFUSAL_CODE

export const roleRefusalMessage = data =>
    (data && (data.Message || data.message)) ||
    'Your role does not allow this action.'
